import {
  MdAccessTime,
  MdAssessment,
  MdDashboard,
  MdDns,
  MdDownload,
  MdFilterAlt,
  MdLayers,
  MdLocationOn,
  MdPeopleAlt,
  MdRefresh,
  MdTrendingUp,
  MdVerifiedUser,
  MdVpnKey,
} from 'react-icons/md';
import { IconType } from 'react-icons';
import { MetricCard, MetricFormat } from '../../components/Monitoring/MetricCard';
import { MetricChart } from '../../components/Monitoring/MetricChart';
import { AccessTimelineChart } from '../../components/Monitoring/AccessTimelineChart';
import { SectionFilter } from '../../components/Monitoring/SectionFilter';
import { GlobalFilter } from '../../components/Monitoring/GlobalFilter';
import { SessionLocationsMap } from '../../components/Monitoring/SessionLocationsMap';
import {
  MonitoringSectionState,
  useMonitoringSections,
} from '../../hooks/useMonitoringSections';
import { MonitoringCards, MonitoringChartData, MonitoringLayerRankingData } from '../../components/Monitoring/types';
import * as Styled from './styles';

type CardDefinition = {
  key: keyof MonitoringCards;
  title: string;
  description?: string;
  format: MetricFormat;
  icon: IconType;
  accent: string;
};

const accessCard: CardDefinition = {
  key: 'acessos_sistema',
  title: 'Acessos ao sistema',
  format: 'number',
  icon: MdPeopleAlt,
  accent: '#017ac6',
};

const averageSessionCard: CardDefinition = {
  key: 'tempo_medio_sessao',
  title: 'Tempo médio de sessão',
  format: 'duration',
  icon: MdAccessTime,
  accent: '#6f5bd3',
};

const accessTypeCard: CardDefinition = {
  key: 'tipo_acesso_mais_utilizado',
  title: 'Tipo de acesso mais utilizado',
  format: 'text',
  icon: MdVpnKey,
  accent: '#22a06b',
};

const dashboardCard: CardDefinition = {
  key: 'acessos_dashboard',
  title: 'Acessos ao dashboard',
  format: 'number',
  icon: MdDashboard,
  accent: '#f39c35',
};

const activatedLayersCard: CardDefinition = {
  key: 'total_camadas_ativadas',
  title: 'Total de camadas ativadas',
  format: 'number',
  icon: MdLayers,
  accent: '#017ac6',
};

const mostUsedLayerCard: CardDefinition = {
  key: 'camada_mais_utilizada',
  title: 'Camada mais utilizada',
  format: 'text',
  icon: MdTrendingUp,
  accent: '#22a06b',
};

const mostFilteredLayerCard: CardDefinition = {
  key: 'camada_mais_filtrada',
  title: 'Camada mais filtrada',
  format: 'text',
  icon: MdFilterAlt,
  accent: '#6f5bd3',
};

const mostDownloadedLayerCard: CardDefinition = {
  key: 'camada_mais_baixada',
  title: 'Camada mais baixada',
  format: 'text',
  icon: MdDownload,
  accent: '#f39c35',
};

const locationPermissionCard: CardDefinition = {
  key: 'percentual_permissao_localizacao',
  title: 'Permissões para obter localização',
  description: 'Percentual de sessões que forneceram localização.',
  format: 'percentage',
  icon: MdLocationOn,
  accent: '#22a06b',
};

const authenticatedSessionsCard: CardDefinition = {
  key: 'percentual_sessoes_autenticadas',
  title: 'Sessões autenticadas',
  format: 'percentage',
  icon: MdVerifiedUser,
  accent: '#6f5bd3',
};

const geoserverCard: CardDefinition = {
  key: 'acessos_geoserver_pelo_mapa',
  title: 'Acessos ao GeoServer pelo mapa',
  description: 'Somente eventos originados na página do mapa; não inclui acessos diretos ao GeoServer.',
  format: 'number',
  icon: MdDns,
  accent: '#e25555',
};

const summaryCards = [
  accessCard,
  averageSessionCard,
  accessTypeCard,
  dashboardCard,
  activatedLayersCard,
  mostUsedLayerCard,
  mostFilteredLayerCard,
  mostDownloadedLayerCard,
];


function getNamedChartData(
  firstColumn: string,
  secondColumn: string,
  items: (MonitoringChartData | MonitoringLayerRankingData)[] = [],
) {
  return [[firstColumn, secondColumn], ...items.map(item => [item.nome, item.valor])];
}

function renderCards(cards: MonitoringCards, definitions: CardDefinition[]) {
  return definitions.map(definition => (
    <MetricCard
      key={definition.key}
      title={definition.title}
      description={definition.description}
      data={cards[definition.key]}
      format={definition.format}
      icon={definition.icon}
      accent={definition.accent}
    />
  ));
}

function SectionFeedback({ state }: { state: MonitoringSectionState }) {
  return (
    <>
      {state.error && <Styled.ErrorBanner role="alert">{state.error}</Styled.ErrorBanner>}
      {state.loading && !state.data && (
        <Styled.LoadingPanel aria-live="polite">
          <MdRefresh aria-hidden="true" />
          <strong>Carregando dados desta seção...</strong>
        </Styled.LoadingPanel>
      )}
    </>
  );
}

export function Monitoring() {
  const { sections, applyFilters, clearFilters, globalFilters, applyGlobalDateFilter, clearGlobalDateFilter } = useMonitoringSections();

  const timeline = sections.accessTimeline.data?.graficos?.acessos_ao_longo_do_tempo;

  const accessTypes = sections.accessTypes.data?.graficos?.tipos_de_acesso;
  const sessionDurations = sections.sessionDuration.data?.graficos?.faixas_de_duracao_das_sessoes;
  const sessionLocations = sections.sessionLocations.data?.graficos?.localizacoes_das_sessoes;
  const mostUsedLayers = sections.mostUsedLayers.data?.graficos?.camadas_mais_utilizadas;
  const displayedMostUsedLayers = mostUsedLayers?.dados.slice(0, 15) ?? [];
  const layerDownloads = sections.layerDownloads.data?.graficos?.downloads_de_camadas;
  const mostUsedFilters = sections.filterUsage.data?.graficos?.filtros_mais_utilizados;
  const filterTypes = sections.filterUsage.data?.graficos?.tipos_de_filtro;

  const getBarHeight = (items: number) => Math.max(340, items * 30 + 90);

  return (
    <Styled.Page>
      <Styled.Container>
        <Styled.PageHeader>
          <div>
            <Styled.Eyebrow>
              <MdAssessment aria-hidden="true" />
              Acesso administrativo
            </Styled.Eyebrow>
            <h1>Módulo de Monitoramento</h1>
            <p>Para filtrar as métricas por período, selecione a data de início e de fim para a busca.</p>
          </div>
        </Styled.PageHeader>

        <GlobalFilter
          filters={globalFilters}
          onApply={applyGlobalDateFilter}
          onClear={clearGlobalDateFilter}
        />

        <Styled.SummarySection>
          <SectionFilter
            id="summary"
            filters={sections.summary.filters}
            loading={sections.summary.loading}
            onApply={filters => applyFilters('summary', filters)}
            onClear={() => clearFilters('summary')}
          />
          <SectionFeedback state={sections.summary} />
          {sections.summary.data?.cards && (
            <Styled.Results $loading={sections.summary.loading} aria-busy={sections.summary.loading}>
              <Styled.CardsGrid>{renderCards(sections.summary.data.cards, summaryCards)}</Styled.CardsGrid>
            </Styled.Results>
          )}
        </Styled.SummarySection>

        <Styled.NumberedSection>
          <SectionFilter
            id="access-timeline"
            filters={sections.accessTimeline.filters}
            loading={sections.accessTimeline.loading}
            onApply={filters => applyFilters('accessTimeline', { ...filters, granularidade: 'auto' })}
            onClear={() => clearFilters('accessTimeline')}
          />
          <SectionFeedback state={sections.accessTimeline} />
          {timeline && (
            <Styled.Results $loading={sections.accessTimeline.loading} aria-busy={sections.accessTimeline.loading}>
              <Styled.FullChart>
                <AccessTimelineChart
                  timeline={timeline}
                  range={sections.accessTimeline.data?.filtros}
                />
              </Styled.FullChart>
            </Styled.Results>
          )}
        </Styled.NumberedSection>

        <Styled.NumberedSection>
          <SectionFilter
            id="access-types"
            filters={sections.accessTypes.filters}
            loading={sections.accessTypes.loading}
            onApply={filters => applyFilters('accessTypes', filters)}
            onClear={() => clearFilters('accessTypes')}
          />
          <SectionFeedback state={sections.accessTypes} />
          {accessTypes && sections.accessTypes.data?.cards && (
            <Styled.Results $loading={sections.accessTypes.loading} aria-busy={sections.accessTypes.loading}>
              <Styled.MixedGrid>
                <MetricChart
                  title="Tipos de acesso"
                  subtitle="Distribuição das sessões entre Gov.br, Google e acesso anônimo."
                  variant="pie"
                  data={getNamedChartData('Tipo de acesso', 'Acessos', accessTypes.dados)}
                  height={400}
                />
                <Styled.SideCards>
                  {renderCards(sections.accessTypes.data.cards, [locationPermissionCard, authenticatedSessionsCard])}
                </Styled.SideCards>
              </Styled.MixedGrid>
            </Styled.Results>
          )}
        </Styled.NumberedSection>

        <Styled.NumberedSection>
          <SectionFilter
            id="session-duration"
            filters={sections.sessionDuration.filters}
            loading={sections.sessionDuration.loading}
            onApply={filters => applyFilters('sessionDuration', filters)}
            onClear={() => clearFilters('sessionDuration')}
          />
          <SectionFeedback state={sections.sessionDuration} />
          {sessionDurations && sections.sessionDuration.data?.cards && (
            <Styled.Results $loading={sections.sessionDuration.loading} aria-busy={sections.sessionDuration.loading}>
              <Styled.MixedGrid>
                <MetricChart
                  title="Duração das sessões"
                  subtitle="Distribuição das sessões por faixa de duração."
                  variant="column"
                  data={getNamedChartData('Faixa de duração', 'Sessões', sessionDurations.dados)}
                />
                <Styled.SideCards>
                  {renderCards(sections.sessionDuration.data.cards, [averageSessionCard])}
                </Styled.SideCards>
              </Styled.MixedGrid>
            </Styled.Results>
          )}
        </Styled.NumberedSection>

        <Styled.NumberedSection>
          <SectionFilter
            id="session-locations"
            filters={sections.sessionLocations.filters}
            loading={sections.sessionLocations.loading}
            onApply={filters => applyFilters('sessionLocations', filters)}
            onClear={() => clearFilters('sessionLocations')}
          />
          <SectionFeedback state={sections.sessionLocations} />
          {sessionLocations && (
            <Styled.Results $loading={sections.sessionLocations.loading} aria-busy={sections.sessionLocations.loading}>
              <Styled.LocationResults>
                <SessionLocationsMap filters={sections.sessionLocations.filters} />
              </Styled.LocationResults>
            </Styled.Results>
          )}
        </Styled.NumberedSection>

        <Styled.NumberedSection>
          <SectionFilter
            id="most-used-layers"
            filters={sections.mostUsedLayers.filters}
            loading={sections.mostUsedLayers.loading}
            showLimit
            maxLimit={15}
            onApply={filters => applyFilters('mostUsedLayers', filters)}
            onClear={() => clearFilters('mostUsedLayers')}
          />
          <SectionFeedback state={sections.mostUsedLayers} />
          {mostUsedLayers && (
            <Styled.Results $loading={sections.mostUsedLayers.loading} aria-busy={sections.mostUsedLayers.loading}>
              <Styled.FullChart>
                {/* Google Charts keeps its initial container size, so recreate it when the height changes. */}
                <MetricChart
                  key={getBarHeight(displayedMostUsedLayers.length)}
                  title="Camadas mais acessadas"
                  subtitle="Quantidade de vezes que cada camada foi ativada."
                  variant="bar"
                  data={getNamedChartData('Camada', 'Ativações', displayedMostUsedLayers)}
                  height={getBarHeight(displayedMostUsedLayers.length)}
                  options={{ chartArea: { height: getBarHeight(displayedMostUsedLayers.length) - 60 } }}
                />
              </Styled.FullChart>
            </Styled.Results>
          )}
        </Styled.NumberedSection>

        <Styled.NumberedSection>
          <SectionFilter
            id="layer-downloads"
            filters={sections.layerDownloads.filters}
            loading={sections.layerDownloads.loading}
            onApply={filters => applyFilters('layerDownloads', { ...filters, limite: 10 })}
            onClear={() => clearFilters('layerDownloads')}
          />
          <SectionFeedback state={sections.layerDownloads} />
          {layerDownloads && sections.layerDownloads.data?.cards && (
            <Styled.Results $loading={sections.layerDownloads.loading} aria-busy={sections.layerDownloads.loading}>
              <Styled.MixedGrid>
                <MetricChart
                  title="Downloads de camadas"
                  subtitle="Quantidade de downloads por camada."
                  variant="bar"
                  data={getNamedChartData('Camada', 'Downloads', layerDownloads.dados)}
                  height={getBarHeight(layerDownloads.dados.length)}
                />
                <Styled.SideCards>
                  {renderCards(sections.layerDownloads.data.cards, [geoserverCard])}
                </Styled.SideCards>
              </Styled.MixedGrid>
            </Styled.Results>
          )}
        </Styled.NumberedSection>

        <Styled.NumberedSection>
          <SectionFilter
            id="filter-usage"
            filters={sections.filterUsage.filters}
            loading={sections.filterUsage.loading}
            onApply={filters => applyFilters('filterUsage', { ...filters, limite: 10 })}
            onClear={() => clearFilters('filterUsage')}
          />
          <SectionFeedback state={sections.filterUsage} />
          {mostUsedFilters && filterTypes && (
            <Styled.Results $loading={sections.filterUsage.loading} aria-busy={sections.filterUsage.loading}>
              <Styled.ChartsGrid>
                <MetricChart
                  title="Camadas mais filtradas"
                  subtitle="Camadas com maior número de filtros aplicados."
                  variant="bar"
                  data={getNamedChartData('Camada', 'Filtros', mostUsedFilters.dados)}
                  height={getBarHeight(mostUsedFilters.dados.length)}
                />
                <MetricChart
                  title="Uso dos filtros simples e avançado"
                  subtitle="Distribuição do uso dos filtros no mapa."
                  variant="pie"
                  data={getNamedChartData('Tipo de filtro', 'Aplicações', filterTypes.dados)}
                  height={getBarHeight(mostUsedFilters.dados.length)}
                />
              </Styled.ChartsGrid>
            </Styled.Results>
          )}
        </Styled.NumberedSection>
      </Styled.Container>
    </Styled.Page>
  );
}
